import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import {
  DropshippingChave,
  DropshippingConfiguracao,
  DropshippingSincronizacao,
  dropshippingApi,
} from "../../../hooks/dropshippingApi";
import { useTheme } from "../../../contexts/Theme/ThemeContext";
import { Input } from "../../Input";
import { Button } from "../../Button";
import { Select } from "../../Select";
import { ContainerDropshipping } from "./styles";

// Mãe gera chaves por catálogo; o revendedor cola a chave e recebe a cópia travada dos produtos.

const MENSAGENS_ERRO: Record<string, string> = {
  NOME_OBRIGATORIO: "Informe um nome para a chave.",
  NOME_MUITO_LONGO: "O nome pode ter no máximo 128 caracteres.",
  CATALOGO_INVALIDO: "Selecione um catálogo ativo e com tabela de preço.",
  SATELITE_NAO_PODE_SER_MAE: "Esta conta recebe produtos de um fornecedor e não pode gerar chaves.",
  CHAVE_NAO_ENCONTRADA: "Chave não encontrada.",
  CHAVE_NAO_INFORMADA: "Informe a chave recebida do fornecedor.",
  CHAVE_INVALIDA: "Chave inválida.",
  CHAVE_JA_UTILIZADA: "Esta chave já está em uso por outra conta.",
  SATELITE_JA_VINCULADA: "Esta conta já está vinculada a um fornecedor.",
  DROPSHIPPING_NAO_PERMITIDO_NO_PLANO_DA_MAE: "O plano do fornecedor não permite o dropshipping.",
  MAE_NAO_PODE_SER_SATELITE:
    "Esta conta tem chaves de fornecedor ativas e não pode receber produtos de outra conta.",
  DESATIVE_A_INTEGRACAO_COM_ERP: "Desative a integração com o Bling ou a Olist antes de vincular.",
  DESATIVE_O_CADASTRO_DE_PRODUTOS_PELA_API:
    "Desative o cadastro de produtos pela Chave de API antes de vincular.",
  SATELITE_POSSUI_PRODUTOS:
    "Para vincular, a conta não pode ter produtos ativos. Inative os produtos cadastrados e tente novamente.",
  DROPSHIPPING_MAE_NAO_PERMITIDO_NO_PLANO: "Recurso disponível no plano Avançado.",
  DROPSHIPPING_SATELITE_NAO_PERMITIDO_NO_PLANO: "Recurso disponível a partir do plano Essencial.",
};

const MOTIVOS_PARADO: Record<string, string> = {
  PLANO_DA_MAE_SEM_DROPSHIPPING: "o plano do fornecedor não permite o dropshipping",
  CATALOGO_INATIVO: "o catálogo liberado está inativo",
  CATALOGO_SEM_TABELA_DE_PRECO: "o catálogo liberado está sem tabela de preço",
};

const mostrarErro = (error: any) => {
  const codigo = error?.response?.data?.error;
  Swal.fire({
    title: "Erro",
    text: MENSAGENS_ERRO[codigo] ?? "Não foi possível concluir. Tente novamente.",
    icon: "error",
  });
};

const formatarData = (data: string | null) =>
  data ? new Date(data.replace(" ", "T")).toLocaleString("pt-BR") : "-";

const textoSincronizacao = (info: DropshippingSincronizacao) => {
  if (!info) return "Aguardando a primeira sincronização";
  switch (info.situacao) {
    case "ok":
      return `Sincronizado (${info.total ?? 0} produtos)`;
    case "sincronizando":
      return `Sincronizando: ${info.pendentes ?? 0} de ${info.total ?? 0} produtos pendentes`;
    case "parado":
      return `Parado: ${MOTIVOS_PARADO[info.motivo ?? ""] ?? info.motivo}`;
    default:
      return "Erro na última sincronização. Uma nova tentativa é feita automaticamente.";
  }
};

interface DropshippingProps {
  onCarregar?: (config: DropshippingConfiguracao) => void;
}

export const Dropshipping: React.FC<DropshippingProps> = ({ onCarregar }) => {
  const { theme } = useTheme();
  const api = dropshippingApi();

  const [config, setConfig] = useState<DropshippingConfiguracao | null>(null);
  const [salvando, setSalvando] = useState(false);

  const [nome, setNome] = useState("");
  const [idCatalogo, setIdCatalogo] = useState("");

  const [editando, setEditando] = useState<number | null>(null);
  const [nomeEdicao, setNomeEdicao] = useState("");
  const [catalogoEdicao, setCatalogoEdicao] = useState("");

  const [chaveSatelite, setChaveSatelite] = useState("");

  const carregar = () =>
    api
      .configuracao()
      .then((dados) => {
        setConfig(dados);
        onCarregar?.(dados);
      })
      .catch((error) => console.log(error));

  useEffect(() => {
    carregar();
  }, []);

  // Atualiza o progresso enquanto a cópia está em andamento.
  const sincronizando =
    config?.satelite?.vinculo?.ativo &&
    (!config.satelite.vinculo.sincronizacao_info ||
      config.satelite.vinculo.sincronizacao_info.situacao === "sincronizando");

  useEffect(() => {
    if (!sincronizando) return;
    const intervalo = setInterval(carregar, 20000);
    return () => clearInterval(intervalo);
  }, [sincronizando]);

  const opcoesCatalogos = [
    { name: "Selecione o catálogo", value: "" },
    ...(config?.mae?.catalogos ?? []).map((c) => ({
      name: `${c.descricao} (tabela: ${c.tabela})`,
      value: String(c.id),
    })),
  ];

  const copiar = (texto: string) => {
    navigator.clipboard.writeText(texto).then(
      () =>
        Swal.fire({
          title: "Chave copiada!",
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        }),
      () =>
        Swal.fire({
          title: "Erro ao copiar a chave!",
          text: "Mas você pode copiar manualmente: " + texto,
          icon: "error",
        })
    );
  };

  const criarChave = async () => {
    setSalvando(true);
    try {
      const nova = await api.criarChave(nome, Number(idCatalogo));
      setNome("");
      setIdCatalogo("");
      await carregar();
      copiar(nova.chave);
    } catch (error) {
      mostrarErro(error);
    } finally {
      setSalvando(false);
    }
  };

  const iniciarEdicao = (chave: DropshippingChave) => {
    setEditando(chave.id);
    setNomeEdicao(chave.nome);
    setCatalogoEdicao(String(chave.id_catalogo));
  };

  const salvarEdicao = async (chave: DropshippingChave) => {
    if (chave.empresa_satelite && Number(catalogoEdicao) !== chave.id_catalogo) {
      const confirmacao = await Swal.fire({
        icon: "warning",
        title: "Trocar o catálogo?",
        text: `Os produtos de ${chave.empresa_satelite} passam a ser os do novo catálogo: o que não estiver nele será inativado na conta dela.`,
        showCancelButton: true,
        confirmButtonText: "Trocar",
        cancelButtonText: "Cancelar",
      });
      if (!confirmacao.isConfirmed) return;
    }

    setSalvando(true);
    try {
      await api.editarChave(chave.id, nomeEdicao, Number(catalogoEdicao));
      setEditando(null);
      await carregar();
    } catch (error) {
      mostrarErro(error);
    } finally {
      setSalvando(false);
    }
  };

  const removerChave = async (chave: DropshippingChave) => {
    const confirmacao = await Swal.fire({
      icon: "warning",
      title: "Remover a chave?",
      text: chave.empresa_satelite
        ? `Os produtos de ${chave.empresa_satelite} param de ser atualizados. Esta ação não pode ser desfeita.`
        : "A chave deixa de funcionar. Esta ação não pode ser desfeita.",
      showCancelButton: true,
      confirmButtonText: "Remover",
      cancelButtonText: "Cancelar",
    });
    if (!confirmacao.isConfirmed) return;

    try {
      await api.removerChave(chave.id);
      await carregar();
    } catch (error) {
      mostrarErro(error);
    }
  };

  const vincular = async () => {
    const confirmacao = await Swal.fire({
      icon: "warning",
      title: "Vincular ao fornecedor?",
      html:
        "Os produtos desta conta passam a ser os do fornecedor e <b>não poderão ser criados nem editados aqui</b>. " +
        "Você continua criando e editando os seus catálogos. Esta ação não pode ser desfeita.",
      showCancelButton: true,
      confirmButtonText: "Vincular",
      cancelButtonText: "Cancelar",
    });
    if (!confirmacao.isConfirmed) return;

    setSalvando(true);
    try {
      const resultado = await api.vincular(chaveSatelite.trim());
      setChaveSatelite("");
      await carregar();
      Swal.fire({
        title: "Conta vinculada!",
        text: `Os produtos de ${resultado.empresa_mae} estão sendo copiados. Isso pode levar alguns minutos.`,
        icon: "success",
      });
    } catch (error) {
      mostrarErro(error);
    } finally {
      setSalvando(false);
    }
  };

  if (!config) return null;

  const vinculo = config.satelite?.vinculo;

  return (
    <ContainerDropshipping theme={theme}>
      <div className="descricao fonteTexto">
        Conecte contas do Conecta Venda: o fornecedor libera um catálogo e gera uma chave para cada
        revendedor. O revendedor informa a chave e recebe os produtos, preços e estoque do
        fornecedor, sempre atualizados, para vender com a marca dele.
      </div>

      {config.mae && (
        <div className="secao">
          <div className="titulo-secao fonteTexto">Sou fornecedor</div>
          <div className="fonteTexto">
            Gere uma chave para cada revendedor. Ele recebe os produtos do catálogo escolhido,
            com o preço da tabela desse catálogo.
          </div>

          <div className="formulario">
            <Input
              type="text"
              label="Nome do revendedor"
              value={nome}
              onChange={(valor) => setNome(valor)}
              placeholder="Ex.: Loja da Maria"
              maxLength={128}
              showMaxLength={false}
            />
            <Select
              label="Catálogo"
              value={idCatalogo}
              options={opcoesCatalogos}
              onChange={(valor) => setIdCatalogo(valor)}
            />
            <Button
              title="Gerar chave"
              width="fit-content"
              loading={salvando}
              disabled={salvando || !nome.trim() || !idCatalogo}
              onClick={criarChave}
            />
          </div>

          <div className="chaves">
            {config.mae.chaves.map((chave) => (
              <div className="chave fonteTexto" key={chave.id}>
                {editando === chave.id ? (
                  <div className="formulario">
                    <Input
                      type="text"
                      label="Nome do revendedor"
                      value={nomeEdicao}
                      placeholder="Ex.: Loja da Maria"
                      onChange={(valor) => setNomeEdicao(valor)}
                      maxLength={128}
                      showMaxLength={false}
                    />
                    <Select
                      label="Catálogo"
                      value={catalogoEdicao}
                      options={opcoesCatalogos}
                      onChange={(valor) => setCatalogoEdicao(valor)}
                    />
                    <div className="acoes">
                      <Button
                        title="Salvar"
                        width="fit-content"
                        loading={salvando}
                        disabled={salvando || !nomeEdicao.trim() || !catalogoEdicao}
                        onClick={() => salvarEdicao(chave)}
                      />
                      <Button
                        title="Cancelar"
                        width="fit-content"
                        onClick={() => setEditando(null)}
                      />
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="cabecalho">
                      <span className="nome">{chave.nome}</span>
                      <div className="acoes fonteRotulos">
                        <span onClick={() => iniciarEdicao(chave)}>Editar</span>
                        <span onClick={() => removerChave(chave)}>Remover</span>
                      </div>
                    </div>
                    <div className="info fonteRotulos">
                      <span>Catálogo: {chave.catalogo ?? "-"}</span>
                      <span>
                        Chave:{" "}
                        <span className="valor-chave" onClick={() => copiar(chave.chave)}>
                          {chave.chave}
                        </span>
                      </span>
                      {chave.empresa_satelite ? (
                        <>
                          <span>
                            Conta vinculada: {chave.empresa_satelite} (desde{" "}
                            {formatarData(chave.data_vinculo)})
                          </span>
                          <span>{textoSincronizacao(chave.sincronizacao_info)}</span>
                        </>
                      ) : (
                        <span>Aguardando o revendedor informar a chave</span>
                      )}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {config.satelite && !config.mae?.chaves.length && (
        <div className="secao">
          <div className="titulo-secao fonteTexto">Sou revendedor</div>

          {vinculo && (
            <div className={`chave fonteTexto${vinculo.ativo ? "" : " desconectado"}`}>
              <div className="cabecalho">
                <span className="nome">Fornecedor: {vinculo.empresa_mae}</span>
                {!vinculo.ativo && <span className="flag-desconectado">Desconectado</span>}
              </div>
              <div className="info fonteRotulos">
                <span>Vinculada desde {formatarData(vinculo.data_vinculo)}</span>
                <span>Produtos recebidos: {vinculo.total_produtos}</span>
                {vinculo.ativo ? (
                  <>
                    <span>{textoSincronizacao(vinculo.sincronizacao_info)}</span>
                    <span>Última sincronização: {formatarData(vinculo.ultima_sincronizacao)}</span>
                  </>
                ) : (
                  <span>
                    O fornecedor encerrou o vínculo em {formatarData(vinculo.data_remocao)}. Os
                    produtos não são mais atualizados.
                  </span>
                )}
              </div>
              <div className="aviso fonteRotulos">
                {vinculo.ativo
                  ? "Os produtos vêm do fornecedor e não podem ser editados nesta conta."
                  : "Os produtos e a tabela de preço que vieram do fornecedor seguem sem edição. Os seus produtos podem ser cadastrados e editados normalmente."}
              </div>
            </div>
          )}

          {(!vinculo || !vinculo.ativo) && (
            <>
              <div className="fonteRotulos">
                {vinculo
                  ? "Informe a nova chave do fornecedor para voltar a receber os produtos."
                  : "Informe a chave gerada pelo fornecedor. Os produtos chegam sem permissão de edição e o preço de venda é definido nos ajustes de cada catálogo."}
              </div>
              <div className="formulario">
                <Input
                  type="text"
                  label="Chave do fornecedor"
                  value={chaveSatelite}
                  onChange={(valor) => setChaveSatelite(valor)}
                  placeholder="Cole aqui a chave"
                  maxLength={64}
                  showMaxLength={false}
                />
                <div />
                <Button
                  title="Vincular"
                  width="fit-content"
                  loading={salvando}
                  disabled={salvando || !chaveSatelite.trim()}
                  onClick={vincular}
                />
              </div>
            </>
          )}
        </div>
      )}
    </ContainerDropshipping>
  );
};
